import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Box, Card, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { makeStyles, useTheme } from '@mui/styles';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import {
  VolumeNumberFormatString as formatVolume,
  EnergyNumberFormatString as formatEnergy,
  CurrencyNumberFormatString as formatCurrency
} from 'src/utils/numbers';
import { APARTMENT_OBJECT_TYPE } from 'src/constants';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { faBolt, faBoltLightning, faChargingStation, faDroplet, faFire, faWater } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  label: {
    marginLeft: theme.spacing(1)
  },
  table: {
    border: 'none'
  },
  tablecell: {
    border: 'none',
    padding: '0px'
  }
}));

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function formatDifference(value) {
  if (value === 0) return "0";
  return value > 0 ? `+${value}` : `${value}`;
}

function SummaryTable({ consumption, lastMonthData, className, isAverage = false, res, ...rest }) {
  //console.log(consumption);
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const { denotation } = useParams();
  const query = useQuery();

  if (!consumption) {
    return null;
  }

  const { periodSummary: summary } = consumption;

  const formatters = {
    volume: formatVolume,
    energy: formatEnergy,
    empty: () => ''
  };

  const formatPriceWithLabel = (priceSum) => {
    const absPrice = Math.abs(priceSum);
    const label = priceSum === 0 ? "" : priceSum < 0 ? 'Att betala' : 'Att få tillbaks';
    return (
      <>
        <Tooltip title={label} placement="bottom" arrow>
        <span>
        <Typography 
          variant=""
          style={{
            color: priceSum < 0 ? "red" : priceSum !== 0 ? "green" : "", 
            fontWeight: "bold" 
          }}
        >
          {formatCurrency(absPrice)}
        </Typography>
        </span>
        </Tooltip>
        {/*<Typography 
          component="span" 
          color="textSecondary" 
          style={{ 
            display: 'inline-block',
          width: priceSum <= 0 ? '60px' : '80px', 
          fontSize: '0.8rem', 
          marginLeft: 4, 
          color: priceSum < 0 ? "red" : "green", 
          fontWeight: "bold" }}
        >
          {label}
        </Typography>*/}
      </>
    );
  };

  const rows = [
    {
      field: 'coldwater',
      text: 'Kallvatten',
      formatter: 'volume',
      visible: consumption.coldWaterSensorsExists,
      icon: faDroplet,
      iconColor: "#0289d1"
    },
    {
      field: 'hotwater',
      text: 'Varmvatten',
      formatter: 'volume',
      visible: consumption.hotWaterSensorsExists,
      icon: faDroplet,
      iconColor: "red"
    },
    {
      field: 'electricity',
      text: 'El',
      formatter: 'energy',
      visible: consumption.electicitySensorsExists,
      icon: faBoltLightning,
      iconColor: "yellow"
    },
    {
      field: 'heatenergy',
      text: 'Värmeenergi',
      formatter: 'energy',
      visible: consumption.heatEnergySensorsExists,
      icon: faFire,
      iconColor: "orange"
    },
    {
      field: 'evCharging',
      text: 'Laddning av elbil',
      formatter: 'energy',
      visible: consumption.evChargingSensorsExists,
      icon: faChargingStation,
      iconColor: "green"
    },
    {
      field: 'kasEnergy',
      text: 'Komfortavräkning',
      formatter: 'empty',
      visible: consumption.measurementObjectTypes?.includes(
        APARTMENT_OBJECT_TYPE.KAS
      )
    }
  ];

  const ConsumptionRow = ({ color, children }) => {
    const childrenWithProps = color
      ? React.Children.map(children, child => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, { color });
          }
          return child;
        })
      : children;

    return <TableRow>{childrenWithProps}</TableRow>;
  };

  const ConsumptionCell = ({ text, icon, iconColor, align, color, link = "" }) => (
    <TableCell sx={{cursor: link !== "" ? "pointer" : ""}} align={align} className={classes.tablecell} onClick={() => link !== "" ? navigate(`/customerview/${link}/${denotation}?${query}`) : null}>
      <Typography variant="h4" color={color || 'textSecondary'}>
        {icon && <FontAwesomeIcon icon={icon} color={iconColor} style={{marginRight: 8}}/>}
        {text}
      </Typography>
    </TableCell>
  );

  const getDifference = (field) => (consumption.periodSummary[field].value - lastMonthData[field].value)?.toFixed(2);
  const getColor = (diff) => diff > 0 ? "red" : diff < 0 ? "green" : "textSecondary";

  return (
    <Card className={clsx(classes.root, className)} {...rest}>
      <Box flexGrow={1}>
        <Typography
          component="h3"
          gutterBottom
          variant="overline"
          color="textSecondary"
        >
          Summering
        </Typography>

        {!isMobile ? (
        <TableContainer>
          <Table className={classes.table} size="small">
            <TableHead>
              <ConsumptionRow color="textPrimary">
                <ConsumptionCell text="Typ" align="left" />
                <ConsumptionCell text="Förbrukning" align="right" />
                <ConsumptionCell text={"Föregående " + (res === 5 ? "dag" : res === 3 ? "år" : "månad")} align="right" />
                <ConsumptionCell text="Skillnad" align="right" />
                {!isAverage && <ConsumptionCell text="Summering" align="right" />}
              </ConsumptionRow>
            </TableHead>
            <TableBody>
              {rows.map(
                row =>
                  row.visible && (
                    <ConsumptionRow key={row.field}>
                      <ConsumptionCell text={row.text} icon={row.icon} iconColor={row.iconColor} link={row.field} align="left" />
                      <ConsumptionCell
                        text={formatters[row.formatter](
                          summary[row.field].value
                        )}
                        align="right"
                      />
                      <ConsumptionCell text={formatters[row.formatter](lastMonthData[row.field].value)} align="right" />
                      <ConsumptionCell text={formatters[row.formatter](formatDifference(getDifference(row.field)))} align="right" color={getColor(getDifference(row.field))} />
                      {!isAverage && <ConsumptionCell
                        text={formatPriceWithLabel(summary[row.field].priceSum)}
                        align="right"
                      />}
                    </ConsumptionRow>
                  )
              )}
              {!isAverage && <>
              <TableRow>
                <TableCell
                  align="right"
                  colSpan={5}
                  className={classes.tablecell}
                >
                  &nbsp;
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  colSpan={4}
                  className={classes.tablecell}
                >
                  <Typography variant="h4" color="textPrimary">
                    Summa
                  </Typography>
                </TableCell>
                <TableCell align="right" className={classes.tablecell}>
                  <Typography variant="h4" style={{
                    color: consumption.totalPriceSum < 0 ? "red" : consumption.totalPriceSum !== 0 ? "green" : "", 
                    fontWeight: "bold" 
                  }}>
                    <Tooltip placement="bottom" arrow title={consumption.totalPriceSum < 0 ? "Att betala" : consumption.totalPriceSum !== 0 ? "Att få tillbaks" : ""}>
                    <span>
                    {formatCurrency(Math.abs(consumption.totalPriceSum))}
                    </span>
                    </Tooltip>
                  </Typography>
                </TableCell>
              </TableRow>
              </>}
            </TableBody>
          </Table>
        </TableContainer>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            {rows.map((row) =>
              row.visible && (
                <Box
                  key={row.field}
                  display="flex"
                  flexDirection="column"
                  p={1}
                  borderBottom={`1px solid ${theme.palette.divider}`}
                >
                  {/* Titel med ikon */}
                  <Typography variant="subtitle2" color="textSecondary">
                    {row.icon && (
                      <FontAwesomeIcon
                        icon={row.icon}
                        color={row.iconColor}
                        style={{ marginRight: 8 }}
                      />
                    )}
                    {row.text}
                  </Typography>

                  {/* Aktuell + Pris */}
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body1">
                      {formatters[row.formatter](summary[row.field].value)}
                    </Typography>
                    {!isAverage && formatPriceWithLabel(summary[row.field].priceSum)}
                  </Box>

                  {/* Föregående månad */}
                  <Box display="flex" justifyContent="space-between" mt={0.5}>
                    <Typography variant="caption" color="textSecondary">
                      Föregående {res === 5 ? "dag" : res === 3 ? "år" : "månad"}
                    </Typography>
                    <Typography variant="caption">
                      {formatters[row.formatter](lastMonthData[row.field].value)}
                    </Typography>
                  </Box>

                  {/* Skillnad */}
                  <Box display="flex" justifyContent="space-between" mt={0.5}>
                    <Typography variant="caption" color="textSecondary">
                      Skillnad
                    </Typography>
                    <Typography
                      variant="caption"
                      color={getColor(getDifference(row.field))}
                      fontWeight="bold"
                    >
                      {formatters[row.formatter](formatDifference(getDifference(row.field)))}
                    </Typography>
                  </Box>
                </Box>
              )
            )}

            {/* Summa */}
            {!isAverage && (
              <Box mt={2} textAlign="right">
                <Typography variant="h6">Summa:</Typography>
                {formatPriceWithLabel(consumption.totalPriceSum)}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Card>
  );
}

SummaryTable.propTypes = {
  className: PropTypes.string
};

export default SummaryTable;
